import getMetricsData from '@api/metricContainer';
import { get } from 'lodash';
import instance from '@api/axiosInstance';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { createQuery } from '@src/components/widget-panel/utils/query';
import useQueryParams from './useQueryParams';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { triggerDeletedFieldsError } from '@utils/errors/triggerPageError';
import { calcDodInterval } from '../utils';

const defaultResponseValue = { totals: {}, data: {} };

function useFetchMetrics({
  metrics = [],
  enableDoD = false,
  panelEnabled = false,
}: {
  metrics: string[];
  enableDoD?: boolean;
  panelEnabled: boolean;
}) {
  const { interval, filter, _rKey } = useQueryParams();

  const allowedMetricList = useMemo(() => {
    let queryMetrics: any = [...metrics];

    queryMetrics.forEach((m) => {
      const invertedMetric = getPercentageMetricByKey({ metricKey: m, defaultValue: null });
      if (invertedMetric) {
        queryMetrics.push(invertedMetric);
      }
    });

    queryMetrics = [...new Set(queryMetrics)].sort();

    return queryMetrics;
  }, [metrics]);

  // Calculate comparison interval (previous period) for DoD
  const comparisonInterval = useMemo(() => {
    if (!enableDoD) return null;
    return calcDodInterval(interval);
  }, [enableDoD, interval]);

  // Current period payload
  const payload = useMemo(() => {
    const options = {
      percentile: 0.9, // Use 90th percentile for metric calculations
    };
    return createQuery(
      allowedMetricList,
      `${interval.from}/${interval.to}`,
      interval.granularity,
      filter,
      options,
    );
  }, [allowedMetricList, interval.from, interval.to, interval.granularity, filter]);

  // DoD period payload
  const dodPayload = useMemo(() => {
    if (!comparisonInterval) return null;

    const options = {
      percentile: 0.9, // Use 90th percentile for DoD metric calculations
    };
    return createQuery(
      allowedMetricList,
      `${comparisonInterval.from}/${comparisonInterval.to}`,
      comparisonInterval.granularity,
      filter,
      options,
    );
  }, [allowedMetricList, comparisonInterval, filter]);

  // Fetch current period data
  const currentDataQuery = useQuery(
    ['dpi-overview-metrics', payload.queries, _rKey],
    () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        triggerDeletedFieldsError(annotations);
        const formattedData = formatTimeseriesData({
          metrics: allowedMetricList,
          data,
          interval,
          annotations,
        });

        return { data: formattedData, totals };
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevent refetch when panel is shown
      enabled: !!metrics.length && panelEnabled,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 5 * 60 * 1000, // 5m
    },
  );

  // Fetch DoD data if enabled
  const dodDataQuery = useQuery(
    ['dpi-overview-dod-metrics', dodPayload?.queries, _rKey],
    () => {
      if (!dodPayload) return defaultResponseValue;

      return getMetricsData(dodPayload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        triggerDeletedFieldsError(annotations);

        const formattedData = formatTimeseriesData({
          metrics: allowedMetricList,
          data,
          interval: comparisonInterval,
          annotations,
        });

        return { data: formattedData, totals };
      });
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevent refetch when panel is shown
      enabled:
        enableDoD &&
        !!metrics.length &&
        panelEnabled &&
        !!comparisonInterval &&
        !!interval?.from &&
        !!interval?.to,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: defaultResponseValue,
    },
  );

  const dodData = useMemo(() => {
    if (!comparisonInterval || !dodDataQuery.data || dodDataQuery.data === defaultResponseValue) {
      return defaultResponseValue;
    }
    return dodDataQuery.data;
  }, [comparisonInterval, dodDataQuery.data]);

  // Return combined data structure
  return {
    ...currentDataQuery,
    dod: enableDoD
      ? {
          data: dodData,
          isFetching: dodDataQuery.isFetching,
          isError: dodDataQuery.isError,
          error: dodDataQuery.error,
          comparisonInterval,
        }
      : null,
  };
}

export default useFetchMetrics;
